import { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';

import notificationApi from '@/core/notifications/api/notification-api';
import { Subscription, TagSchemaField } from '@/core/notifications/model';
import {
  buildTagFilterFromValues,
  invalidTagValues,
  missingRequiredTags,
  toValueList,
} from '@/core/notifications/model/tag-filter-utils';
import TagFieldCard from '@/core/notifications/components/subscription-wizard/tag-field-card';

function tagValuesFromFilter(tagFilter: Record<string, unknown>): Record<string, string[]> {
  const values: Record<string, string[]> = {};
  Object.entries(tagFilter).forEach(([key, value]) => {
    values[key] = toValueList(value);
  });
  return values;
}

function requiredFieldKeys(tagSchema: Record<string, TagSchemaField>): Set<string> {
  return new Set(
    Object.entries(tagSchema)
      .filter(([, field]) => field.required)
      .map(([key]) => key),
  );
}

// The form itself, with no modal chrome. Exported separately so tests can
// exercise it directly — see quick-edit-name-popover.tsx for why.
// `QuickEditFiltersPopover` below is the thin Modal wrapper (a centered
// dialog, not an anchored popover — see quick-edit-name-popover.tsx for why).
//
// Unlike the wizard's own Step 2, there's no "Receive everything / Narrow the
// scope" toggle here — this popover only exists to edit filters on a
// subscription that's already being created, so the field cards are always
// shown directly; clearing every value on an optional field already means
// "accept any value" for it, same as picking "Receive everything" would.
// Reuses TagFieldCard (the wizard's own field-card piece) so each field still
// looks and behaves exactly like it does in the create/edit wizard.
export function QuickEditFiltersPopoverContent({
  subscription,
  tagSchema,
  onClose,
  onSaved,
}: {
  subscription: Subscription;
  tagSchema: Record<string, TagSchemaField>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [tagValues, setTagValues] = useState<Record<string, string[]>>(() =>
    tagValuesFromFilter(subscription.tagFilter),
  );
  // required fields start expanded, same UX decision as the wizard's scope step
  const [expandedFieldKeys, setExpandedFieldKeys] = useState<Set<string>>(() =>
    requiredFieldKeys(tagSchema),
  );
  const [saving, setSaving] = useState(false);

  const toggleExpanded = (tagKey: string) => {
    setExpandedFieldKeys(prev => {
      const next = new Set(prev);
      if (next.has(tagKey)) next.delete(tagKey);
      else next.add(tagKey);
      return next;
    });
  };

  const toggleLeaf = (tagKey: string, value: string) => {
    setTagValues(prev => {
      const current = prev[tagKey] ?? [];
      const next = current.includes(value)
        ? current.filter(existing => existing !== value)
        : [...current, value];
      return { ...prev, [tagKey]: next };
    });
  };

  const toggleGroup = (tagKey: string, leafKeys: string[], checked: boolean) => {
    setTagValues(prev => {
      const cleared = (prev[tagKey] ?? []).filter(key => !leafKeys.includes(key));
      return { ...prev, [tagKey]: checked ? [...cleared, ...leafKeys] : cleared };
    });
  };

  const setFreeText = (tagKey: string, raw: string) => {
    setTagValues(prev => ({
      ...prev,
      [tagKey]: raw
        .split(',')
        .map(part => part.trim())
        .filter(part => part.length > 0),
    }));
  };

  const missingRequired = missingRequiredTags(tagSchema, tagValues);
  const hasInvalidValues = Object.keys(invalidTagValues(tagSchema, tagValues)).length > 0;
  const canSave = missingRequired.length === 0 && !hasInvalidValues && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await notificationApi.updateSubscription(subscription.id, {
        tagFilter: buildTagFilterFromValues(tagValues),
      });
      toast.success('Filters updated');
      onSaved();
    } catch (e) {
      toast.error('Could not update filters');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="mb-3">
        {missingRequired.length > 0 && (
          <span className="text-danger d-block mb-1" style={{ fontSize: 12 }}>
            {missingRequired.length} required
          </span>
        )}
        <div className="mutedText" style={{ fontSize: 12 }}>
          Only notifications matching all fields are delivered. Multiple values in a field match any
          of them. Leave optional fields blank to accept any value.
        </div>
      </div>

      <div>
        {Object.entries(tagSchema).map(([tagKey, field]) => (
          <TagFieldCard
            key={tagKey}
            tagKey={tagKey}
            field={field}
            selectedValues={tagValues[tagKey] ?? []}
            expanded={expandedFieldKeys.has(tagKey)}
            onToggleExpanded={() => toggleExpanded(tagKey)}
            onToggleLeaf={value => toggleLeaf(tagKey, value)}
            onToggleGroup={(leafKeys, checked) => toggleGroup(tagKey, leafKeys, checked)}
            onFreeText={raw => setFreeText(tagKey, raw)}
          />
        ))}
      </div>

      <div className="d-flex justify-content-end gap-2 mt-2">
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={onClose}
          disabled={saving}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-sm btn-tertiary text-base-light"
          onClick={handleSave}
          disabled={!canSave}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </>
  );
}

// Quick edit #3 of 3 on a subscription row (see subscription-list.tsx): the
// wizard's field cards, scoped to this one subscription's filter. PATCHes
// only `tagFilter`.
export default function QuickEditFiltersPopover({
  subscription,
  tagSchema,
  onClose,
  onSaved,
}: {
  subscription: Subscription;
  tagSchema: Record<string, TagSchemaField>;
  onClose: () => void;
  onSaved: () => void;
}) {
  return (
    <Modal show onHide={onClose} centered className="notifications-settings">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 14, fontWeight: 700 }}>Filters</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        <QuickEditFiltersPopoverContent
          subscription={subscription}
          tagSchema={tagSchema}
          onClose={onClose}
          onSaved={onSaved}
        />
      </Modal.Body>
    </Modal>
  );
}
